import { ElementRef } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { format } from 'date-fns';

import { GOAL_PROGRESSES, OKR_TYPES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { sendDownloadCSVEvent } from 'constants/mixpanelHelper';

import { convertToCSV, parseCSV } from '../../Insights/helpers';

export const generateGoalsExploreCSVData = ({
  exportedRows,
  sideNavSelectedCohort,
  gridRef,
  moduleName,
  keyResultsName,
  objectivesName,
}: {
  exportedRows: 'all' | 'filteredAndSorted';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sideNavSelectedCohort: any;
  gridRef: ElementRef<typeof AgGridReact>;
  moduleName: string;
  keyResultsName: string;
  objectivesName: string;
}) => {
  sendDownloadCSVEvent({
    eventProperties: {
      source: 'goals',
      cohortId: sideNavSelectedCohort?.id,
      cohortName: sideNavSelectedCohort?.name,
      page:
        sideNavSelectedCohort?.name === 'My Goals'
          ? `${sideNavSelectedCohort?.name} explore`
          : `Goals ${sideNavSelectedCohort?.name} explore`,
      reportType: exportedRows === 'all' ? 'new report detailed' : 'new report filtered',
    },
  });
  //   @ts-expect-error current is possibly null
  const data = gridRef?.current?.api.getDataAsCsv({
    // fileName: DEFAULT_GROUP_BY_INDIVIDUAL_FILE_NAME,
    allColumns: true,
    processCellCallback(params) {
      const { value, node, column } = params ?? {};
      const colData = node?.data;

      if (column?.colId === 'ag-Grid-AutoColumn') {
        // Replace commas with a special character § which is not used in any of the data
        return value?.title?.replace(/,/g, '§');
      }

      if (column?.colId === 'type') {
        if (value === OKR_TYPES.OBJECTIVE) {
          return modifyString(objectivesName, StringModifier.TITLE_CASE);
        }
        return modifyString(keyResultsName, StringModifier.TITLE_CASE);
      }

      if (column?.colId === 'table-team') {
        return value
          ?.map((item) => item)
          ?.filter((item) => item)
          ?.join(` `);
      }

      if (column?.colId === 'table-tags') {
        return value
          ?.map((item) => item)
          ?.filter((item) => item)
          ?.join(` `);
      }

      if (['table-start-date', 'table-due-date'].includes(column?.colId)) {
        return value ? format(new Date(value), 'MMM dd yyyy') : 'Never';
      }
      if (['table-owner', 'table-collaborators'].includes(column?.colId)) {
        const { toIds } = node.data;

        if (column?.colId === 'table-owner') {
          return toIds
            ?.map(({ email, ownerType }) => {
              if (ownerType === 'OWNER') return email;
              return null;
            })
            ?.filter((item) => item)
            ?.join(` `);
        }
        if (column?.colId === 'table-collaborators') {
          return toIds
            ?.map(({ email, ownerType }) => {
              if (ownerType === 'COLLABORATOR') return email;
              return null;
            })
            ?.filter((item) => item)
            ?.join(` `);
        }
      }
      if (column?.colId === 'table-status') {
        const statusData = colData?.progressLevel;

        const label = GOAL_PROGRESSES.find((item) => item.value === statusData)?.label ?? statusData;

        return label;
      }

      if (column?.colId === 'status') {
        const statusData = colData?.progress?.progressCount;
        return JSON.stringify(statusData).replaceAll(',', ';'); // replacing comma with semicolon as we use comma delimited csv.
      }
      if (column?.colId === 'companies') {
        return value?.map((item) => item.companyName).join(', ');
      }
      return value;
    },

    exportedRows,
  });

  const parsedCsv = parseCSV(data);

  const updatedCsv = parsedCsv.map((row) => {
    // deleting keys Goal Title, Progress %age from row object

    const modifiedRow = { ...row };

    let goalTitle = modifiedRow[`${modifyString(moduleName, StringModifier.TITLE_CASE)}`];
    goalTitle = goalTitle?.replace(/§/g, ',');

    delete modifiedRow[`Child ${keyResultsName} Status`];
    delete modifiedRow['Progress %age\r'];
    delete modifiedRow['Goal Title'];

    return {
      ...modifiedRow,
      [`${modifyString(moduleName, StringModifier.TITLE_CASE)}`]: goalTitle,
    };
  });

  const csvData = convertToCSV(updatedCsv);
  const blob = new Blob([csvData], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${moduleName}_Explore_${Date.now()}.csv`;
  a.click();
};

const DEFAULT_GROUP_BY_INDIVIDUAL_FILE_NAME = `IndividualInsights_${Date.now()}.csv`;

export const generateIndividualInsightsCSVData = ({
  exportedRows,
  sideNavSelectedCohort,
  gridRef,
  fileName = DEFAULT_GROUP_BY_INDIVIDUAL_FILE_NAME,
}: {
  exportedRows: 'all' | 'filteredAndSorted';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sideNavSelectedCohort: any;
  gridRef: ElementRef<typeof AgGridReact>;
  fileName?: string;
}) => {
  sendDownloadCSVEvent({
    eventProperties: {
      source: 'goals',
      page:
        sideNavSelectedCohort?.name === 'My Goals'
          ? `${sideNavSelectedCohort?.name} insights`
          : `Goals ${sideNavSelectedCohort?.name} insights`,
      reportType: exportedRows === 'all' ? 'new report detailed' : 'new report filtered',
    },
  });
  //   @ts-expect-error current is possibly null
  gridRef?.current?.api.exportDataAsCsv({
    fileName,
    allColumns: true,
    processCellCallback(params) {
      const { value, node, column } = params ?? {};
      const colData = node?.data;

      if (column?.colId === 'table-last-checkin') {
        return value ? format(new Date(value), 'MMM dd yyyy') : 'Never';
      }
      if (column?.colId === 'table-status') {
        const statusData = colData?.status;

        return Object.keys(statusData)
          .filter((key) => ['ON_TRACK', 'OFF_TRACK', 'CLOSED', 'AT_RISK'].includes(key))
          .map((key) => {
            const label = GOAL_PROGRESSES.find((item) => item.value === key)?.label ?? key;
            return `${label}: ${statusData[key]?.COUNT}`;
          })
          .join(', ');
      }
      if (column?.colId === 'company') {
        return value?.companyName;
      }

      return value;
    },
    processHeaderCallback(params) {
      const { column } = params ?? {};

      // It should be headerName as per ag-grid documentation
      // Refer: https://www.ag-grid.com/react-data-grid/column-headers/#header-name
      // But BE is sending displayName in some APIs
      return column?.colDef?.headerName ?? column?.colDef?.displayName;
    },

    exportedRows,
  });
};
